import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getDetalleFactura } from "./api"
import { TIPO_COMPROBANTE } from "./helpers"
import ResumenTab from "./tabs/ResumenTab"
import LineasTab from "./tabs/LineasTab"
import PagosTab from "./tabs/PagosTab"
import CargosTab from "./tabs/CargosTab"
import CorreosTab from "./tabs/CorreosTab"

const TABS = [
  { id: "resumen",  label: "Resumen",     icon: "mdi-view-dashboard-outline" },
  { id: "lineas",   label: "Líneas",      icon: "mdi-format-list-bulleted",  count: d => (d?.data || []).length },
  { id: "pagos",    label: "Pagos",       icon: "mdi-credit-card-multiple",  count: d => (d?.metodos_pago || []).length },
  { id: "cargos",   label: "Cargos",      icon: "mdi-tag-text-outline",      count: d => (d?.cargos || []).length },
  { id: "correos",  label: "Correos",     icon: "mdi-email-multiple-outline", count: d => (d?.correos || []).length },
]

const CopiarMini = ({ texto }) => {
  const [copiado, setCopiado] = useState(false)
  if (!texto) return null
  const copiar = async (e) => {
    e.stopPropagation()
    try { await navigator.clipboard.writeText(texto); setCopiado(true); setTimeout(() => setCopiado(false), 1500) }
    catch { /* noop */ }
  }
  return (
    <button
      type="button" onClick={copiar}
      title={copiado ? "Copiado" : "Copiar"}
      style={{
        background: "none", border: "none", padding: 2, cursor: "pointer",
        color: copiado ? "#10b981" : "rgba(255,255,255,0.6)",
        display: "inline-flex", alignItems: "center",
      }}
    >
      <i className={`mdi mdi-${copiado ? "check" : "content-copy"}`} style={{ fontSize: 14 }} />
    </button>
  )
}

CopiarMini.propTypes = { texto: PropTypes.string }

/**
 * Drawer lateral con el detalle completo de una factura.
 * Hace su propio fetch a /facturas/{cufe}/detalles.
 *
 * Props:
 *  - abierto: bool
 *  - cufe: string (CUFE de la factura a cargar)
 *  - onCerrar: function
 */
const DetalleFacturaDrawer = ({ abierto, cufe, onCerrar }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  // Cargar al abrir + reset al cerrar
  useEffect(() => {
    if (!abierto || !cufe) return
    let cancelled = false
    setLoading(true)
    setError("")
    setData(null)
    setTabActivo("resumen")
    getDetalleFactura(cufe)
      .then(res => {
        if (cancelled) return
        const payload = res.data
        if (payload?.success === false) {
          throw new Error(payload?.message || payload?.error || "Error al cargar el detalle.")
        }
        setData(payload)
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.response?.data?.message || err.response?.data?.error || err.message || "No se pudo cargar el detalle.")
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [abierto, cufe])

  // Esc cierra
  useEffect(() => {
    if (!abierto) return
    const h = e => { if (e.key === "Escape") onCerrar() }
    window.addEventListener("keydown", h)
    return () => window.removeEventListener("keydown", h)
  }, [abierto, onCerrar])

  if (!abierto) return null

  const cabecera = data?.cabecera || {}
  const tipoLabel = TIPO_COMPROBANTE[cabecera.tipocomprobante] || cabecera.descripcion_tipo || cabecera.tipocomprobante
  const folioCompleto = cabecera.prefijo || cabecera.folio
    ? `${cabecera.prefijo || ""}-${cabecera.folio || ""}`
    : null

  return (
    <>
      <div
        onClick={onCerrar}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15, 23, 42, 0.5)",
          zIndex: 1040,
          animation: "fadeIn 0.2s ease",
        }}
      />
      <div
        style={{
          position: "fixed", top: 0, right: 0,
          height: "100vh", width: "min(1100px, 95vw)",
          background: "#f8fafc", zIndex: 1050,
          boxShadow: "-8px 0 32px rgba(15, 23, 42, 0.15)",
          display: "flex", flexDirection: "column",
          animation: "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <style>{`
          @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        {/* Header */}
        <div style={{
          padding: "16px 24px",
          background: "linear-gradient(135deg, #0f172a, #1e3a5f)",
          color: "#fff",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16,
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Detalle de factura
            </div>
            {folioCompleto ? (
              <h4 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#fff", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                {folioCompleto}
                {tipoLabel && (
                  <span style={{
                    marginLeft: 12, fontSize: 12, fontWeight: 500,
                    background: "rgba(255,255,255,0.15)",
                    padding: "3px 10px", borderRadius: 6,
                    fontFamily: "inherit",
                  }}>
                    {tipoLabel}
                  </span>
                )}
              </h4>
            ) : (
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}>
                {loading ? "Cargando…" : "Detalle"}
              </h4>
            )}
            {cufe && (
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                  CUFE: {cufe.substring(0, 24)}…
                </span>
                <CopiarMini texto={cufe} />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            style={{
              background: "rgba(255,255,255,0.1)", border: "none",
              color: "#fff", width: 36, height: 36, borderRadius: 8,
              cursor: "pointer",
              fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
            title="Cerrar (Esc)"
          >
            <i className="mdi mdi-close" />
          </button>
        </div>

        {/* Tabs */}
        {data && (
          <div style={{
            background: "#fff", borderBottom: "1px solid #e2e8f0",
            padding: "0 24px",
            display: "flex", gap: 4, overflowX: "auto",
          }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              const count = tab.count ? tab.count(data) : null
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTabActivo(tab.id)}
                  style={{
                    background: "none", border: "none",
                    padding: "12px 16px",
                    fontSize: 13, fontWeight: 600,
                    color: activo ? "#0f172a" : "#64748b",
                    borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                    cursor: "pointer", transition: "color 0.15s",
                    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  }}
                >
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                  {count != null && count > 0 && (
                    <span style={{
                      background: activo ? "#3b82f6" : "#e2e8f0",
                      color: activo ? "#fff" : "#64748b",
                      padding: "1px 7px", borderRadius: 999,
                      fontSize: 10, fontWeight: 700,
                    }}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading && (
            <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
              <Spinner color="primary" />
              <div className="mt-2">Cargando detalle…</div>
            </div>
          )}

          {error && (
            <div style={{
              padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
              color: "#991b1b", borderRadius: 10,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
              <span>{error}</span>
            </div>
          )}

          {data && !loading && (
            <>
              {tabActivo === "resumen"  && <ResumenTab data={data} />}
              {tabActivo === "lineas"   && <LineasTab data={data} />}
              {tabActivo === "pagos"    && <PagosTab data={data} />}
              {tabActivo === "cargos"   && <CargosTab data={data} />}
              {tabActivo === "correos"  && <CorreosTab data={data} />}
            </>
          )}
        </div>
      </div>
    </>
  )
}

DetalleFacturaDrawer.propTypes = {
  abierto: PropTypes.bool.isRequired,
  cufe: PropTypes.string,
  onCerrar: PropTypes.func.isRequired,
}

export default DetalleFacturaDrawer
